import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import area from '@turf/area'
import { MapRef } from 'react-map-gl'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import { FeatureCollection, Polygon } from 'geojson'
import {
  usePermitTemplatesFilter,
  usePermitStatusFilter,
  useDateRangeFilter,
  useFilterValueStore,
} from '@core/providers/filters'
import { useProjectMapPermits } from '@core/react-query/features/permits'
import { DialogContent, Divider, Popper } from '@mui/material'
import { Flex, Box } from '@fundamentals'
import { PermitStatus } from '@core/entities/permit/PermitBase'
import { GeoLocationArea } from '@core/entities/map'
import useVisibleEntitiesOnMap from '@core/providers/useVisibleEntitiesOnMap'
import { areasToGeojson, geojsonToAreas } from '@core/utils/geolocation-helpers'
import { PermitCard } from '@elements'
import { Map } from '@elements/Geolocation'
import { useToast } from '@core/toast'
import { useModal } from '@common/GlobalModal'
import { ModalDialogContainer } from '@common/modal'
import ConfirmModal from '@common/modal/ConfirmModal'
import { Button, DialogTitle, IconButton } from '@common/material'
import {
  tryCatch,
  calculateMapBounds,
  calculatePositionOfPopper,
  setupClickEffect,
  setupHoverEffect,
  zoomToFeatures,
  hashFeatureCollection,
  getFlatVertexIndex,
  PolygonEastingNorthingList,
  parseArcGisCsv,
  ArcGisCsvParseError,
} from '@modules/geolocation'
import { GeoPointMarkerLayer, GeoPolygonLayer } from '@elements/Geolocation'
import {
  PermitFilterToolbar,
  PermitListSidebar,
  usePermitMapFeatures,
  permitMapLayerIds,
  permitMapSourceIds,
  PERMIT_MARKERS_ID,
  PERMIT_ZONES_ID,
} from '@modules/geolocation/permits'
import { DrawControlToolbar } from './DrawControlToolbar'
import { ImportArcGisCsvModal } from './ImportArcGisCsvModal'
import { projectMapGeoDrawAreaStyles } from './ProjectMapGeoDrawAreaStyle'
import { localizedStrings } from '@core/strings'
import { PermitDetailsModal } from '@modules/permitDetails/PermitDetailsModal'
import { Permit } from '@core/entities/permit/Permit'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import ClearIcon from '@mui/icons-material/Clear'
import { InitialViewState } from '@core/entities/project/ProjectLocationNode/ProjectLocationNodeBoundary'

// mapbox-gl-draw renders each style layer twice, suffixing the id with
// `.hot`/`.cold` (actively-edited vs settled features). These are the vertex
// point layers we hit-test against to detect hovering over a polygon corner.
const drawVertexLayerIds = [
  'gl-draw-polygon-and-line-vertex-active.hot',
  'gl-draw-polygon-and-line-vertex-active.cold',
]

// Screen-space radius around the cursor within which a drawn vertex counts
// as hovered.
const DRAW_VERTEX_HIT_RADIUS_PX = 6

// Maps a hovered mapbox-gl-draw vertex (its parent feature id + coord_path,
// e.g. "0.2" = ring 0, vertex 2) to its position in the flattened vertex
// order shown by PolygonEastingNorthingList (see getPolygonVertices).
const locateDrawnVertex = (
  collection: FeatureCollection<Polygon> | undefined,
  parentId: unknown,
  coordPath: unknown,
): { polygonIndex: number; vertexIndex: number } | null => {
  if (!collection || typeof coordPath !== 'string') return null

  const featureIndex = collection.features.findIndex(
    (f) => String(f.id) === String(parentId),
  )
  if (featureIndex === -1) return null

  const [ringPart, vertexPart] = coordPath.split('.')
  const ringIndex = Number(ringPart)
  const vertexInRing = Number(vertexPart)
  if (!Number.isInteger(ringIndex) || !Number.isInteger(vertexInRing)) {
    return null
  }

  const vertexIndex = getFlatVertexIndex(
    collection.features[featureIndex],
    ringIndex,
    vertexInRing,
  )
  if (vertexIndex === null) {
    return null
  }

  return {
    polygonIndex: featureIndex + 1,
    vertexIndex,
  }
}

type ProjectMapGeoDrawAreaModalProps = {
  projectId: number
  organisationId: number
  initialFilters?: {
    permitStatuses?: PermitStatus[]
    permitTemplate?: number[]
  }
  initialGeoLocationAreas?: GeoLocationArea[]
  onChangeGeoLocationAreas?: (geoLocationAreas: GeoLocationArea[]) => void
  readonly?: boolean
  initialViewState?: InitialViewState | null
}

export const ProjectMapGeoDrawAreaModal: React.FC<
  ProjectMapGeoDrawAreaModalProps
> = ({
  projectId,
  organisationId,
  initialFilters = {
    permitStatuses: [
      'OPEN',
      'REQUESTED',
      'ISSUED',
      'EXTENSION_REQUESTED',
      'SENT_TO_PERMIT_HOLDER',
    ],
  },
  initialGeoLocationAreas,
  onChangeGeoLocationAreas,
  readonly,
  initialViewState,
}) => {
  const mapRef = useRef<MapRef>()
  const drawRef = useRef<MapboxDraw>(
    new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        trash: false,
      },
      styles: projectMapGeoDrawAreaStyles,
    }),
  )
  // Track if the draw control has been added to the map and is ready
  const isDrawReadyRef = useRef<boolean>(false)
  // Store the cleanup function for the click effect
  const cleanupClickEffectRef = useRef<(() => void) | null>(null)
  const cleanupDrawSelectionRef = useRef<(() => void) | null>(null)
  const cleanupVertexHoverRef = useRef<(() => void) | null>(null)
  const { showModal, hideModal, hideAllModals } = useModal()
  const toast = useToast()
  const [mode, setMode] = useState<'draw' | 'close' | 'confirm'>('draw')
  const [featureCount, setFeatureCount] = useState(0)
  const [calculatedAreaInSquareMeters, setCalculatedAreaInSquareMeters] =
    useState(0)
  const [drawnFeatureCollection, setDrawnFeatureCollection] = useState<
    FeatureCollection<Polygon> | undefined
  >(undefined)
  const [selectedDrawnPolygonIndex, setSelectedDrawnPolygonIndex] = useState<
    number | null
  >(null)
  const [hoveredDrawnVertex, setHoveredDrawnVertex] = useState<{
    polygonIndex: number
    vertexIndex: number
  } | null>(null)
  // Ref mirror so the selectionchange listener always sees the latest
  // features without having to be re-bound when drawings change.
  const drawnFeatureCollectionRef = useRef<
    FeatureCollection<Polygon> | undefined
  >(undefined)
  useEffect(() => {
    drawnFeatureCollectionRef.current = drawnFeatureCollection
  }, [drawnFeatureCollection])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [canDelete, setCanDelete] = useState(false)
  const [initialHash, setInitialHash] = useState(null)
  const [showExistingPermitsOnMap, setShowExistingPermitsOnMap] = useState(true)
  const [hoveredEntityId, setHoveredEntityId] = useState(null)
  const [popupEntity, setPopupEntity] = useState<{
    id: number
    clientX: number
    clientY: number
  }>(null)

  const filters = {
    status: usePermitStatusFilter({
      multiple: true,
    }),
    permitTemplate: usePermitTemplatesFilter({
      isPinned: true,
      label: localizedStrings.permitType,
      multiple: true,
      scope: {
        type: 'PROJECT',
        organisationId,
        projectId,
      },
    }),
    dateRange: useDateRangeFilter({
      isPinned: true,
      label: localizedStrings.dateRange,
      dataTest: 'date-range-filter',
    }),
  }

  const store = useFilterValueStore({
    filters,
    initialValues: {
      status: initialFilters?.permitStatuses,
      permitTemplate: initialFilters?.permitTemplate,
    },
  })

  const { data: permitsWithGeolocation } = useProjectMapPermits(
    {
      projectId,
      templateIds: store.values.permitTemplate?.length
        ? store.values.permitTemplate
        : null,
      permitStatuses: store.values.status?.length ? store.values.status : null,
      startTime: store?.values?.dateRange?.startTime || null,
      endTime: store?.values?.dateRange?.endTime || null,
    },
    {
      onError: (e) => {
        toast.error(e.message)
      },
    },
  )

  const { setMapBounds, visibleEntitiesOnMap: visiblePermitsOnMap } =
    useVisibleEntitiesOnMap(permitsWithGeolocation)

  const { zones: permitZones, markers: permitMarkers } = usePermitMapFeatures(
    permitsWithGeolocation,
  )

  const hoveredPermit = useMemo(() => {
    if (!hoveredEntityId && !permitsWithGeolocation) {
      return null
    }

    return permitsWithGeolocation?.find(
      (permit) => permit.id === hoveredEntityId,
    )
  }, [hoveredEntityId])

  const popupPermit = useMemo(() => {
    if (!popupEntity) {
      return null
    }

    return permitsWithGeolocation?.find(
      (permit) => permit.id === popupEntity.id,
    )
  }, [popupEntity])

  const handleOnPermitPopupDetails = useCallback((permit: Permit) => {
    showModal(
      <PermitDetailsModal
        permitId={permit.id}
        organisationId={permit.organisationId}
      />,
    )
    setPopupEntity(null)
  }, [])

  const handleOnMapClick = (event, feature) => {
    // Guard: ensure draw control is initialized
    if (!isDrawReadyRef.current) {
      return
    }

    const selectedDrawnAreaIds = drawRef?.current?.getSelectedIds()
    setCanDelete(!!selectedDrawnAreaIds?.length)

    tryCatch(() => {
      const currentMode = drawRef?.current?.getMode()

      if (feature && currentMode === 'simple_select') {
        const clientX = event.originalEvent.clientX
        const clientY = event.originalEvent.clientY

        setPopupEntity({
          id: feature.properties.permitId,
          clientX,
          clientY,
        })

        return
      }

      setPopupEntity(null)
    })
  }

  const onLoad = () => {
    mapRef.current.addControl(drawRef.current)

    // Mark draw control as ready after it's been added to the map
    isDrawReadyRef.current = true

    mapRef.current.on('draw.create', onDrawUpdate)
    mapRef.current.on('draw.delete', onDrawUpdate)
    mapRef.current.on('draw.update', onDrawUpdate)
    mapRef.current.on('draw.modechange', onMapboxDrawModeChange)

    if (initialGeoLocationAreas) {
      const initialGeoJson = areasToGeojson(initialGeoLocationAreas)

      setInitialHash(hashFeatureCollection(initialGeoJson))

      if (initialGeoJson.features.length > 0) {
        // Calculate the area of the drawn polygons in square meters
        const calculatedSquareMeters = Math.round(area(initialGeoJson))
        setCalculatedAreaInSquareMeters(calculatedSquareMeters)
        setDrawnFeatureCollection(initialGeoJson as FeatureCollection<Polygon>)

        const bounds = calculateMapBounds(initialGeoJson.features)
        mapRef.current.fitBounds(bounds, { animate: false })

        drawRef?.current?.add(initialGeoJson)
      }
    }

    const bounds = mapRef.current.getBounds()
    setMapBounds({
      nw: bounds.getNorthWest(),
      se: bounds.getSouthEast(),
    })

    setupHoverEffect(
      mapRef.current,
      permitMapLayerIds,
      permitMapSourceIds,
      (permitId, action) => {
        if (action === 'select') {
          setHoveredEntityId(permitId)
          return
        }

        if (action === 'deselect') {
          setHoveredEntityId(null)
        }
      },
    )

    // Store the cleanup function for the click effect
    cleanupClickEffectRef.current = setupClickEffect(
      mapRef.current,
      handleOnMapClick,
    )

    // Highlight the matching coordinate in the list while the cursor is over a
    // drawn polygon's corner. mapbox-gl-draw only renders vertices while a
    // polygon is selected for editing, so this lights up during direct edit.
    const handleVertexHover = (e) => {
      const map = mapRef.current?.getMap()
      if (!map || !isDrawReadyRef.current) return

      const vertexLayerIds = drawVertexLayerIds.filter((id) => map.getLayer(id))

      let located: { polygonIndex: number; vertexIndex: number } | null = null
      if (vertexLayerIds.length > 0) {
        const hitRadius = DRAW_VERTEX_HIT_RADIUS_PX
        const vertexFeature = map
          .queryRenderedFeatures(
            [
              [e.point.x - hitRadius, e.point.y - hitRadius],
              [e.point.x + hitRadius, e.point.y + hitRadius],
            ],
            { layers: vertexLayerIds },
          )
          .find((f) => f.properties?.meta === 'vertex')

        if (vertexFeature) {
          located = locateDrawnVertex(
            drawnFeatureCollectionRef.current,
            vertexFeature.properties.parent,
            vertexFeature.properties.coord_path,
          )
        }
      }

      setHoveredDrawnVertex((prev) =>
        prev?.polygonIndex === located?.polygonIndex &&
        prev?.vertexIndex === located?.vertexIndex
          ? prev
          : located,
      )
    }
    mapRef.current.on('mousemove', handleVertexHover)
    cleanupVertexHoverRef.current = () => {
      mapRef.current?.off('mousemove', handleVertexHover)
    }

    // Highlight the corresponding row in the coordinates list whenever the
    // user selects a drawn polygon via mapbox-gl-draw.
    const onDrawnSelectionChange = (e: {
      features?: { id?: string | number }[]
    }) => {
      const selectedId = e.features?.[0]?.id
      if (selectedId === undefined || selectedId === null) {
        setSelectedDrawnPolygonIndex(null)
        return
      }
      const features = drawnFeatureCollectionRef.current?.features ?? []
      const idx = features.findIndex((f) => f.id === selectedId)
      setSelectedDrawnPolygonIndex(idx >= 0 ? idx + 1 : null)
    }
    mapRef.current.on('draw.selectionchange', onDrawnSelectionChange)
    cleanupDrawSelectionRef.current = () => {
      mapRef.current?.off('draw.selectionchange', onDrawnSelectionChange)
    }
  }

  const onMoveEnd = () => {
    const bounds = mapRef.current.getBounds()
    setMapBounds({
      nw: bounds.getNorthWest(),
      se: bounds.getSouthEast(),
    })
  }

  const syncDrawState = () => {
    // Guard: ensure draw control is initialized
    if (!isDrawReadyRef.current || !drawRef?.current) {
      return
    }

    const { data: featureCollection, error } = tryCatch(() =>
      drawRef.current.getAll(),
    )
    if (error) {
      return
    }

    setFeatureCount(featureCollection?.features?.length ?? 0)
    setDrawnFeatureCollection(
      featureCollection as FeatureCollection<Polygon> | undefined,
    )

    // Calculate the area of the drawn polygons in square meters
    const calculatedSquareMeters = featureCollection
      ? Math.round(area(featureCollection))
      : 0
    setCalculatedAreaInSquareMeters(calculatedSquareMeters)

    // Hash the current feature collection and compare with the initial hash
    const currentHash = hashFeatureCollection(featureCollection)
    setHasUnsavedChanges(currentHash !== initialHash)
  }

  const onDrawUpdate = (event) => {
    syncDrawState()

    setMode('draw')
  }

  const onMapboxDrawModeChange = (event) => {
    if (event.mode === 'simple_select') {
      setMode('draw')
    }
  }

  const handleOnDrawNewArea = () => {
    // Guard: ensure draw control is initialized before changing modes
    if (!isDrawReadyRef.current || !drawRef?.current) {
      return
    }

    tryCatch(() => {
      drawRef.current.changeMode('simple_select')
      drawRef.current.changeMode('draw_polygon')

      setMode('close')
      setCanDelete(false)
    })
  }

  const handleOnOpenImportArcGisCsvModal = () => {
    showModal(<ImportArcGisCsvModal onImport={handleOnImportArcGisCsvFile} />)
  }

  const handleOnImportArcGisCsvFile = async (file: File) => {
    // Guard: ensure draw control is initialized
    if (!isDrawReadyRef.current || !drawRef?.current) {
      return
    }

    try {
      const csv = await file.text()
      const importedFeatureCollection = parseArcGisCsv(csv)

      drawRef.current.add(importedFeatureCollection)

      const bounds = calculateMapBounds(importedFeatureCollection.features)
      mapRef.current?.fitBounds(bounds, { animate: false })

      syncDrawState()
      setMode('draw')

      toast.success(
        localizedStrings.importFromArcGisCsvSuccess(
          importedFeatureCollection.features.length,
        ),
      )
    } catch (error) {
      const message =
        error instanceof ArcGisCsvParseError
          ? error.message
          : localizedStrings.genericError
      toast.error(localizedStrings.importFromArcGisCsvError(message))
    }
  }

  const handleOnDeleteArea = () => {
    // Guard: ensure draw control is initialized
    if (!isDrawReadyRef.current || !drawRef?.current) {
      return
    }

    tryCatch(() => {
      const selectedFeatureIds = drawRef.current.getSelectedIds()
      drawRef.current.delete(selectedFeatureIds)

      syncDrawState()

      setCanDelete(false)
      setMode('draw')
    })
  }

  const handleOnCloseArea = () => {
    // Guard: ensure draw control is initialized
    if (!isDrawReadyRef.current || !drawRef?.current) {
      return
    }

    tryCatch(() => {
      drawRef.current.changeMode('simple_select')
      setMode('draw')
    })
  }

  const handleOnSaveAreas = () => {
    // Guard: ensure draw control is initialized
    if (!isDrawReadyRef.current || !drawRef?.current) {
      return
    }

    tryCatch(() => {
      const featureCollection = drawRef.current.getAll()

      zoomToFeatures({
        mapRef: mapRef,
        features: featureCollection?.features,
        radius: 60,
      })

      setMode('confirm')

      return featureCollection
    })
  }

  const handleOnConfirmAreas = () => {
    // Guard: ensure draw control is initialized
    if (!isDrawReadyRef.current || !drawRef?.current) {
      return
    }

    tryCatch(() => {
      const featureCollection = drawRef.current.getAll()

      const areas = geojsonToAreas(featureCollection)

      onChangeGeoLocationAreas(areas)

      setMode('draw')

      hideModal()
    })
  }

  const handleOnCloseModal = () => {
    if (hasUnsavedChanges) {
      showModal(
        <ConfirmModal
          title={localizedStrings.unsavedChanges}
          dismissText={localizedStrings.noExit}
          confirmText={localizedStrings.saveNow}
          onDismiss={() => {
            hideAllModals()
          }}
          onConfirm={() => {
            handleOnConfirmAreas()
            hideAllModals()
          }}
          onClose={() => {
            hideModal()
          }}
        >
          {localizedStrings.ifYouExitWithoutSaving}
        </ConfirmModal>,
      )

      return
    }

    hideModal()
  }

  // Cleanup listeners to prevent memory leaks when component unmounts
  useEffect(() => {
    return () => {
      // Mark draw control as no longer ready
      isDrawReadyRef.current = false

      // Clean up click effect listener
      if (cleanupClickEffectRef.current) {
        cleanupClickEffectRef.current()
        cleanupClickEffectRef.current = null
      }

      if (cleanupDrawSelectionRef.current) {
        cleanupDrawSelectionRef.current()
        cleanupDrawSelectionRef.current = null
      }

      if (cleanupVertexHoverRef.current) {
        cleanupVertexHoverRef.current()
        cleanupVertexHoverRef.current = null
      }

      if (mapRef.current) {
        mapRef.current.off('draw.create', onDrawUpdate)
        mapRef.current.off('draw.delete', onDrawUpdate)
        mapRef.current.off('draw.update', onDrawUpdate)
        mapRef.current.off('draw.modechange', onMapboxDrawModeChange)
      }
    }
  }, [])

  return (
    <ModalDialogContainer
      PaperProps={{
        sx: {
          maxWidth: '90%',
          height: '95vh',
          maxHeight: '95vh',
        },
      }}
      onClose={handleOnCloseModal}
    >
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.pointOfWorks}
        <IconButton aria-label='close-modal' onClick={handleOnCloseModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Flex flexDirection='column' height='100%' width='100%'>
          <Flex>
            <PermitFilterToolbar
              filters={filters}
              store={store}
              showPermits={showExistingPermitsOnMap}
              setShowPermits={setShowExistingPermitsOnMap}
            />
          </Flex>

          <Flex flex={1} sx={{ minHeight: 0 }}>
            <Flex flexDirection='column' flex={1} mr={2} sx={{ minHeight: 0 }}>
              <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                <Map
                  mapRef={mapRef}
                  projectId={projectId}
                  onLoad={onLoad}
                  onMoveEnd={onMoveEnd}
                  initialViewState={
                    !initialGeoLocationAreas?.length && !!initialViewState
                      ? initialViewState
                      : undefined
                  }
                  serverConfig={
                    !initialGeoLocationAreas?.length && !!initialViewState
                      ? { initialViewState: false }
                      : undefined
                  }
                  style={{ height: '100%', width: '100%' }}
                >
                  {showExistingPermitsOnMap &&
                    permitZones?.features?.length !== 0 && (
                      <GeoPolygonLayer
                        key={PERMIT_ZONES_ID}
                        id={PERMIT_ZONES_ID}
                        promoteId='permitId'
                        featureCollection={permitZones}
                      />
                    )}

                  {showExistingPermitsOnMap &&
                    permitMarkers?.features?.length !== 0 && (
                      <GeoPointMarkerLayer
                        key={PERMIT_MARKERS_ID}
                        id={PERMIT_MARKERS_ID}
                        promoteId='permitId'
                        featureCollection={permitMarkers}
                      />
                    )}
                </Map>
              </Box>

              <PolygonEastingNorthingList
                featurePolygon={drawnFeatureCollection}
                highlightedIndex={selectedDrawnPolygonIndex}
                highlightedVertex={hoveredDrawnVertex}
              />
            </Flex>

            {popupPermit && (
              <Popper
                id={'popup'}
                open={true}
                anchorEl={calculatePositionOfPopper(
                  popupEntity.clientX,
                  popupEntity.clientY,
                )}
                // Necessary so that the element is on top of modals
                style={{ zIndex: 15000 }}
              >
                <Box width={'350px'}>
                  <PermitCard
                    templateName={popupPermit?.template?.name}
                    {...popupPermit}
                    sx={{ mx: 1, mt: 1 }}
                  >
                    <Flex mt={1}>
                      <Button
                        variant='contained'
                        size='small'
                        sx={{ flexGrow: 1, mr: 1 }}
                        endIcon={<KeyboardArrowRightIcon />}
                        onClick={() => handleOnPermitPopupDetails(popupPermit)}
                      >
                        {localizedStrings.details}
                      </Button>
                    </Flex>
                  </PermitCard>
                </Box>
              </Popper>
            )}

            {!popupPermit && mode !== 'close' && hoveredPermit && (
              <Box
                width='300px'
                overflow='auto'
                position='absolute'
                sx={{ pointerEvents: 'none' }}
              >
                <PermitCard
                  templateName={hoveredPermit?.template?.name}
                  {...hoveredPermit}
                  sx={{ mx: 1, mt: 1, borderColor: 'primary.main' }}
                />
              </Box>
            )}

            <Box width='300px' overflow='auto' position='relative' mr={-2}>
              <PermitListSidebar
                mapRef={mapRef}
                permits={visiblePermitsOnMap}
                hoveredEntityId={hoveredEntityId}
                setHoveredEntityId={setHoveredEntityId}
              />
            </Box>
          </Flex>
        </Flex>
      </DialogContent>

      {!readonly && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Divider />
          <DrawControlToolbar
            mode={mode}
            onDrawNewArea={handleOnDrawNewArea}
            onDeleteArea={handleOnDeleteArea}
            onCloseArea={handleOnCloseArea}
            onSaveAreas={handleOnSaveAreas}
            onConfirmAreas={handleOnConfirmAreas}
            onImportArcGisCsv={handleOnOpenImportArcGisCsvModal}
            featureCount={featureCount}
            squareMeters={calculatedAreaInSquareMeters}
            canDelete={canDelete}
          />
        </Box>
      )}
    </ModalDialogContainer>
  )
}
